import { useCallback, useState } from 'react';
import { useMutation } from 'urql';
import { JobStatus, RescheduleJobDocument } from '~/gql/graphql';
import { RescheduleDetails } from './types';

export type UseRescheduleResult = {
  isRescheduling: boolean;
  dialogIsOpen: boolean;
  rescheduleDetails?: RescheduleDetails;
  openDialog: (details: RescheduleDetails) => void;
  closeDialog: () => void;
  handleReschedule: (
    details: RescheduleDetails,
    isCurrentUser: (id: string) => boolean
  ) => void;
  handleImmediateReschedule: (details: RescheduleDetails) => void;
  handleDialogSubmit: (
    details: RescheduleDetails,
    args: { comment?: string; notify?: boolean }
  ) => void;
};

export const useReschedule = (): UseRescheduleResult => {
  const [, rescheduleJob] = useMutation(RescheduleJobDocument);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleDetails, setRescheduleDetails] =
    useState<RescheduleDetails>();

  const revertOptimistic = useCallback((details: RescheduleDetails) => {
    // Placeholder for per-view optimistic revert if needed
    console.log('Reverting optimistic update for:', details.jobId);
  }, []);

  const applyOptimistic = useCallback((details: RescheduleDetails) => {
    // Placeholder for per-view optimistic apply if needed
    console.log('Applying optimistic update for:', details.jobId);
  }, []);

  const handleImmediateReschedule = useCallback(
    (details: RescheduleDetails) => {
      setIsRescheduling(true);
      rescheduleJob({
        input: { id: details.jobId, offsetDays: details.offsetDays },
      })
        .then((result) => {
          if (result.error) {
            revertOptimistic(details);
          }
        })
        .catch(() => revertOptimistic(details))
        .finally(() => setIsRescheduling(false));
    },
    [rescheduleJob, revertOptimistic]
  );

  const handleReschedule = useCallback(
    (details: RescheduleDetails, isCurrentUser: (id: string) => boolean) => {
      applyOptimistic(details);
      if (details.jobStatus === JobStatus.Offered) {
        setRescheduleDetails(details);
        setDialogIsOpen(true);
        return;
      }
      if (details.jobStatus === JobStatus.Accepted) {
        if (details.assigneeId && isCurrentUser(details.assigneeId)) {
          handleImmediateReschedule(details);
        } else {
          setRescheduleDetails(details);
          setDialogIsOpen(true);
        }
        return;
      }
      handleImmediateReschedule(details);
    },
    [applyOptimistic, handleImmediateReschedule]
  );

  const openDialog = useCallback((details: RescheduleDetails) => {
    setRescheduleDetails(details);
    setDialogIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogIsOpen(false);
    setRescheduleDetails(undefined);
    setIsRescheduling(false);
  }, []);

  const handleDialogSubmit = useCallback(
    (
      details: RescheduleDetails,
      args: { comment?: string; notify?: boolean }
    ) => {
      setIsRescheduling(true);
      rescheduleJob({
        input: {
          id: details.jobId,
          offsetDays: details.offsetDays,
          comment: args.comment || undefined,
          // notify: args.notify || undefined,
        },
      })
        .then((result) => {
          if (result.error) {
            revertOptimistic(details);
          } else {
            setDialogIsOpen(false);
            setRescheduleDetails(undefined);
          }
        })
        .catch(() => {
          revertOptimistic(details);
        })
        .finally(() => setIsRescheduling(false));
    },
    [rescheduleJob, revertOptimistic]
  );

  return {
    isRescheduling,
    dialogIsOpen,
    rescheduleDetails,
    openDialog,
    closeDialog,
    handleReschedule,
    handleImmediateReschedule,
    handleDialogSubmit,
  };
};

export default useReschedule;
